import { useLocation } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Clock,
  Cloud,
  Lock,
  LifeBuoy,
  Settings,
  ChevronRight,
  Plus,
} from 'lucide-react';

import { CATEGORIES, localizedLabel, getActiveCategory } from '@/lib/toolCategories';
import { useFileDialog } from '@/hooks/useFileDialog';
import { useAuth } from '@/hooks/useAuth';
import { useContactPanel } from '@/contexts/ContactPanelContext';
import UpdateStatusRow from './UpdateStatusRow';

/** Map account.tier → human-readable plan label for the account chip. */
function planLabel(tier) {
  if (!tier) return 'Free';
  if (tier === 'lifetime_pro') return 'Pro Lifetime';
  if (tier === 'lifetime_lite') return 'Lite Lifetime';
  if (tier === 'pro') return 'Pro';
  if (tier === 'lite') return 'Lite';
  return 'Free';
}

/** Single nav row — button with data-nav / data-active attributes. */
function NavRow({ icon: Icon, label, active, onClick, children, style }) {
  return (
    <button
      data-nav
      data-active={active ? 'true' : 'false'}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        height: '36px',
        padding: '0 12px',
        borderRadius: '9px',
        fontSize: '13.5px',
        color: active ? 'var(--ds-indigo-strong)' : 'var(--ds-ink-2)',
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
        position: 'relative',
        userSelect: 'none',
        width: '100%',
        border: 'none',
        textAlign: 'left',
        background: active ? 'var(--ds-content)' : 'transparent',
        boxShadow: active ? 'var(--ds-shadow-card, 0 1px 2px rgba(20,22,48,.05))' : 'none',
        ...style,
      }}
    >
      {Icon && (
        <Icon
          size={17}
          style={{ color: active ? 'var(--ds-indigo)' : 'var(--ds-ink-3)', flexShrink: 0 }}
        />
      )}
      {label}
      {children}
    </button>
  );
}

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { openFile } = useFileDialog();
  const { user, account } = useAuth();
  const { open: openContact } = useContactPanel();

  /** Open-a-PDF inline handler (mirrors the Task 3.1 shared helper). */
  async function handleOpenPdf() {
    const r = await openFile('.pdf');
    if (r) {
      const file = new File([r.buffer], r.name, { type: 'application/pdf' });
      navigate('/viewer', { state: { file, filePath: r.path, cloudDocId: r.cloudDocId || null } });
    }
  }

  const activeCategory = getActiveCategory(location.pathname);
  const isHomeActive = location.pathname === '/tools';
  const isRecentActive = location.pathname === '/recent';
  const isCloudActive = location.pathname === '/cloud';
  const hasCloud = !!(account?.capabilities?.cloud_bytes);

  // Account display info
  const displayName = user?.displayName || account?.email || '';
  const emailStr = account?.email || user?.email || '';
  const initial = emailStr ? emailStr[0].toUpperCase() : '?';
  const tierLabel = planLabel(account?.tier);

  return (
    <aside
      style={{
        width: '250px',
        flexShrink: 0,
        background: 'var(--ds-sidebar)',
        borderRight: '1px solid var(--ds-line)',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 12px 12px',
      }}
    >
      {/* Brand mark + wordmark */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '4px 8px 14px',
        }}
      >
        <div
          style={{
            width: '30px',
            height: '30px',
            borderRadius: '8px',
            background: 'linear-gradient(140deg, var(--ds-indigo), var(--ds-violet))',
            color: 'var(--ds-content, #fff)',
            fontWeight: 700,
            fontSize: '17px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(var(--ds-indigo-rgb, 79,70,229), .32)',
            flexShrink: 0,
          }}
        >
          S
        </div>
        <span
          style={{
            fontSize: '16.5px',
            fontWeight: 600,
            letterSpacing: '-0.2px',
          }}
        >
          Scrivar
        </span>
      </div>

      {/* Open a PDF — primary action button */}
      <button
        onClick={handleOpenPdf}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          height: '40px',
          margin: '0 4px 16px',
          background: 'linear-gradient(140deg, var(--ds-indigo), var(--ds-indigo-strong))',
          color: 'var(--ds-content, #fff)',
          border: 'none',
          borderRadius: '10px',
          fontFamily: 'inherit',
          fontSize: '13.5px',
          fontWeight: 600,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(var(--ds-indigo-rgb, 79,70,229), .30)',
          letterSpacing: '.1px',
        }}
      >
        <Plus size={17} />
        Open a PDF
      </button>

      {/* Home */}
      <NavRow
        icon={Home}
        label="Home"
        active={isHomeActive}
        onClick={() => navigate('/tools')}
      />

      {/* Recent files */}
      <NavRow
        icon={Clock}
        label="Recent files"
        active={isRecentActive}
        onClick={() => navigate('/recent')}
      />

      {/* TOOLS section */}
      <div
        style={{
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '.7px',
          textTransform: 'uppercase',
          color: 'var(--ds-ink-4)',
          padding: '14px 12px 6px',
        }}
      >
        Tools
      </div>

      {CATEGORIES.map((cat) => {
        const Icon = cat.icon;
        const isCatActive = activeCategory === cat.id;
        // 'more' → show all tools; others → show their category launcher
        const dest = cat.id === 'more' ? '/launch/all' : `/launch/${cat.id}`;
        return (
          <NavRow
            key={cat.id}
            icon={Icon}
            label={localizedLabel(t, cat)}
            active={isCatActive}
            onClick={() => navigate(dest)}
          />
        );
      })}

      {/* WORKSPACE section */}
      <div
        style={{
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '.7px',
          textTransform: 'uppercase',
          color: 'var(--ds-ink-4)',
          padding: '14px 12px 6px',
        }}
      >
        Workspace
      </div>

      {/* Cloud */}
      <NavRow
        icon={Cloud}
        label="Cloud"
        active={isCloudActive}
        onClick={() => navigate('/cloud')}
      >
        {!hasCloud && (
          <Lock
            size={14}
            style={{ marginLeft: 'auto', color: 'var(--ds-ink-4)' }}
          />
        )}
      </NavRow>

      {/* Flexible spacer */}
      <div style={{ flex: 1 }} />

      {/* Footer */}
      <div
        style={{
          borderTop: '1px solid var(--ds-line)',
          paddingTop: '10px',
          marginTop: '8px',
        }}
      >
        <UpdateStatusRow />

        {/* Help & contact */}
        <NavRow
          icon={LifeBuoy}
          label="Help & contact"
          active={false}
          onClick={openContact}
        />

        {/* Settings */}
        <NavRow
          icon={Settings}
          label="Settings"
          active={location.pathname === '/account'}
          onClick={() => navigate('/account')}
        />

        {/* Account chip */}
        <button
          onClick={() => navigate('/account')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px',
            borderRadius: '11px',
            cursor: 'pointer',
            marginTop: '4px',
            width: '100%',
            border: 'none',
            background: 'transparent',
            textAlign: 'left',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--ds-hover, rgba(20,22,48,.045))';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          {/* Avatar */}
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '9px',
              background: 'linear-gradient(140deg, var(--ds-mark-grad-1, #5B53E8), var(--ds-mark-grad-2, #8B7FF0))',
              color: 'var(--ds-content, #fff)',
              fontWeight: 600,
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {initial}
          </div>

          {/* Name + plan */}
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: 1.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'var(--ds-ink)',
              }}
            >
              {displayName}
            </div>
            <div
              style={{
                fontSize: '11.5px',
                color: 'var(--ds-ink-3)',
                lineHeight: 1.25,
              }}
            >
              {tierLabel}
            </div>
          </div>

          <ChevronRight
            size={15}
            style={{ marginLeft: 'auto', color: 'var(--ds-ink-4)', flexShrink: 0 }}
          />
        </button>
      </div>
    </aside>
  );
}
